import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-2 mb-6"
          >
            ← Back to Superwhere
          </Link>
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-slate-400">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">1. Introduction</h2>
            <p className="text-slate-300 leading-relaxed">
              Welcome to Superwhere ("we," "our," or "us"). Superwhere is the Global Builder & Superteam Directory for the Solana ecosystem. 
              We are committed to protecting your privacy and ensuring transparency about how we collect, use, and protect your information.
            </p>
            <p className="text-slate-300 leading-relaxed">
              This Privacy Policy explains how we handle your personal information when you use our platform, available at 
              <a href="https://superwhere.io" className="text-purple-400 hover:underline"> superwhere.io</a> and our mobile application.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-200">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li><strong>Authentication Data:</strong> When you sign in using X/Twitter OAuth, we receive your Twitter username, profile information, and authentication tokens.</li>
              <li><strong>Profile Information:</strong> Information you choose to provide in your builder profile, including:
                <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                  <li>Display name and bio</li>
                  <li>Skills and expertise</li>
                  <li>Social media links and website URLs</li>
                  <li>Country representation</li>
                  <li>Superteam memberships</li>
                </ul>
              </li>
              <li><strong>User Content:</strong> Any content you create, post, or share on the platform, including profile updates and community interactions.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">2.2 Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li><strong>Usage Data:</strong> Information about how you interact with our platform, including pages visited, features used, and time spent.</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, and device identifiers.</li>
              <li><strong>Location Data:</strong> General location information based on your country selection and IP address (for geographic representation on the globe).</li>
              <li><strong>Log Data:</strong> Server logs containing IP addresses, access times, and request details.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">3. How We Use Your Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Provide and maintain our platform services</li>
              <li>Create and manage your builder profile</li>
              <li>Display your profile and country representation on the interactive globe</li>
              <li>Facilitate connections between builders and Superteam communities</li>
              <li>Send you important updates about the platform (if you opt in)</li>
              <li>Improve our services and user experience</li>
              <li>Analyze usage patterns and platform performance</li>
              <li>Ensure platform security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">4. Data Sharing and Disclosure</h2>
            <p className="text-slate-300 leading-relaxed mb-4">We may share your information in the following circumstances:</p>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-200">4.1 Public Information</h3>
            <p className="text-slate-300 leading-relaxed">
              Your builder profile information (name, bio, skills, links, country, and Superteam memberships) is publicly visible 
              to other users of the platform as part of our directory service.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">4.2 Service Providers</h3>
            <p className="text-slate-300 leading-relaxed">
              We may share information with third-party service providers who assist us in operating our platform, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Hosting and cloud infrastructure providers</li>
              <li>Authentication services (X/Twitter OAuth)</li>
              <li>Analytics and monitoring services</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-purple-200 mt-6">4.3 Legal Requirements</h3>
            <p className="text-slate-300 leading-relaxed">
              We may disclose information if required by law, court order, or government regulation, or to protect our rights, 
              property, or safety, or that of our users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">5. Data Storage and Security</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Encryption of data in transit using SSL/TLS</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication requirements</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to 
              protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">6. Your Rights and Choices</h2>
            <p className="text-slate-300 leading-relaxed mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Update or correct your profile information at any time through your account settings</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications (if applicable)</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              To exercise these rights, please contact us at <a href="mailto:architecture@obeliskprotocol.io" className="text-purple-400 hover:underline">architecture@obeliskprotocol.io</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">7. Third-Party Services</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Our platform integrates with third-party services that have their own privacy policies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li><strong>X/Twitter:</strong> We use X/Twitter OAuth for authentication. Your use of X/Twitter is subject to 
                <a href="https://twitter.com/en/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline"> Twitter's Privacy Policy</a>.</li>
              <li><strong>Solana Blockchain:</strong> Some features may interact with the Solana blockchain, which is a public, decentralized network.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">8. Cookies and Tracking Technologies</h2>
            <p className="text-slate-300 leading-relaxed">
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, and maintain your session. 
              You can control cookies through your browser settings, though this may affect platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">9. Children's Privacy</h2>
            <p className="text-slate-300 leading-relaxed">
              Our platform is not intended for users under the age of 13. We do not knowingly collect personal information from 
              children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">10. International Data Transfers</h2>
            <p className="text-slate-300 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. By using our 
              platform, you consent to the transfer of your information to these countries.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date. Your continued use of the platform after such 
              changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-purple-300">12. Contact Us</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/20">
              <p className="text-slate-300">
                <strong className="text-purple-300">Email:</strong>{' '}
                <a href="mailto:architecture@obeliskprotocol.io" className="text-purple-400 hover:underline">
                  architecture@obeliskprotocol.io
                </a>
              </p>
              <p className="text-slate-300 mt-2">
                <strong className="text-purple-300">Support Email:</strong>{' '}
                <a href="mailto:architecture@obeliskprotocol.io" className="text-purple-400 hover:underline">
                  architecture@obeliskprotocol.io
                </a>
              </p>
              <p className="text-slate-300 mt-2">
                <strong className="text-purple-300">Website:</strong>{' '}
                <a href="https://superwhere.io" className="text-purple-400 hover:underline">
                  https://superwhere.io
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
